import PageHeader from "../components/PageHeader";
import EditorialMarquee from "../components/EditorialMarquee";
import CTASection from "../components/CTASection";
import { Reveal, RevealWordsOnScroll } from "../components/Reveal";
import { IMAGES } from "../data/site";
import { UtensilsCrossed, Wine, Salad, Cake, Soup, Flame } from "lucide-react";

const STYLES = [
  { icon: UtensilsCrossed, t: "Plated Dinners", d: "Elegant multi-course service brought to every table by our attentive staff." },
  { icon: Soup, t: "Lavish Buffets", d: "Generous, beautifully presented buffets with live carving and refresh throughout." },
  { icon: Flame, t: "Chef Stations", d: "Interactive stations — pasta, carving, dessert — that keep guests delighted." },
  { icon: Salad, t: "Cultural Menus", d: "Halal, South Asian, Mediterranean, Latin & more — authentic and made in-house." },
  { icon: Wine, t: "Full Bar Service", d: "Signature cocktails, wine & beer packages with professional bartenders." },
  { icon: Cake, t: "Sweets & Cakes", d: "Dessert tables, wedding cakes and late-night bites, all coordinated for you." },
];

const MENUS = [
  { name: "Hors d'Oeuvres", items: ["Seasonal crudité & cheese", "Stuffed mushrooms", "Coconut shrimp", "Spinach & feta phyllo"] },
  { name: "Entrées", items: ["Herb-roasted chicken", "Braised short rib", "Grilled salmon", "Vegetable biryani"] },
  { name: "Sides & Salads", items: ["Saffron rice pilaf", "Garlic mashed potato", "Mediterranean salad", "Roasted seasonal veg"] },
  { name: "Desserts", items: ["Assorted French pastries", "Baklava & kunafa", "Chocolate fountain", "Custom wedding cake"] },
];

export default function Catering() {
  return (
    <div data-testid="catering-page">
      <PageHeader
        overline="In-House Catering"
        titleLines={["Cuisine crafted", <span key="i" className="italic text-gold">in-house</span>]}
        subtitle="No outside caterers required. Our culinary team designs every menu from scratch — plated dinners, lavish buffets, chef stations and cultural cuisine — with full bar service and dietary accommodations."
        image={IMAGES.cateringSpread}
        imageAlt="Vista Banquet Hall in-house catering spread"
        testId="catering-page-header"
      />

      {/* service styles */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-8 md:pb-16">
        <div className="mb-14 max-w-2xl">
          <span className="overline text-gold">How We Serve</span>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight text-navy">
            <RevealWordsOnScroll text="Every style of service, under one roof." />
          </h2>
        </div>
        <div className="grid gap-px bg-gold/25 border border-gold/25 sm:grid-cols-2 lg:grid-cols-3">
          {STYLES.map((s, i) => (
            <Reveal key={s.t} delay={(i % 3) * 0.08} className="bg-ivory p-8 md:p-10">
              <s.icon size={26} className="text-gold" />
              <h3 className="mt-5 font-serif text-2xl md:text-3xl font-light text-navy">{s.t}</h3>
              <p className="mt-3 text-navy/60 font-light leading-relaxed">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee items={["Plated & Buffet", "Cultural Cuisine", "Full Bar", "Made In-House"]} />

      {/* sample menu */}
      <section className="bg-navy text-ivory">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-12 md:px-10 py-24 md:py-32">
          <div className="md:col-span-4">
            <span className="overline text-gold">A Taste of Vista</span>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              Sample menu selections.
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-6 text-ivory/65 font-light leading-relaxed">
                A glimpse of what's possible — every menu is fully customized to your
                celebration, guest count and cultural traditions.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 overflow-hidden rounded-sm border border-ivory/15 aspect-[4/3]">
                <img src={IMAGES.catering} alt="Vista catering presentation" className="h-full w-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-8 md:pl-8">
            <div className="grid gap-10 sm:grid-cols-2">
              {MENUS.map((m, i) => (
                <Reveal key={m.name} delay={i * 0.08}>
                  <div className="border-t border-gold/40 pt-5">
                    <h3 className="overline text-gold">{m.name}</h3>
                    <ul className="mt-4 space-y-2.5">
                      {m.items.map((it) => (
                        <li key={it} className="font-serif text-xl font-light text-ivory/90">{it}</li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* signature bar & beverages */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <div className="mb-14 max-w-2xl">
          <span className="overline text-gold">Raise a Glass</span>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight text-navy">
            <RevealWordsOnScroll text="Signature bar & handcrafted beverages." />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-navy/60 font-light leading-relaxed">
              From welcome drinks styled to your palette to a full bar with professional
              bartenders — every pour is presented as beautifully as it tastes.
            </p>
          </Reveal>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { src: "/venue/z04.webp", alt: "Signature drinks styled with red roses" },
            { src: "/venue/z08.webp", alt: "Handcrafted golden cocktails with rose accents" },
            { src: "/venue/up_catering.webp", alt: "Beverage and hors d'oeuvres presentation" },
          ].map((d, i) => (
            <Reveal key={d.src} delay={i * 0.1}>
              <div className="group overflow-hidden rounded-sm border border-gold/25 aspect-[4/5]">
                <img src={d.src} alt={d.alt} className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection
        dark={false}
        overline="Let's Talk Menus"
        heading="Design your menu with our chefs."
        body="Request pricing or book a tasting during your private tour."
        actions={[{ to: "/pricing", label: "Request Pricing", primary: true }, { to: "/tour", label: "Schedule a Tour" }]}
        testId="cta-catering-page"
      />
    </div>
  );
}
